import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Trash2, Copy, Calendar,
  Loader2, Users, DollarSign, Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['Boys', 'Girls', 'Mixed'];
const FORMATS = ['single_elimination', 'double_elimination', 'round_robin', 'pool_play'];

// Template Card
const TemplateCard = ({ template, onUse, onDelete }) => (
  <Card className="hover:border-primary/50 transition-colors">
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">{template.name}</CardTitle>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="text-destructive h-8 w-8"
          onClick={() => onDelete(template.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {template.description && (
        <CardDescription className="mt-1">{template.description}</CardDescription>
      )}
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {template.age_categories?.map(cat => (
          <Badge key={cat} variant="outline">{cat}</Badge>
        ))}
        <Badge variant="secondary">{template.gender}</Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-1 text-muted-foreground">
          <Users className="h-3 w-3" />
          {template.max_teams} teams max
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <DollarSign className="h-3 w-3" />
          ${template.registration_fee} fee
        </div>
      </div>
      
      <Badge className="capitalize">{template.format?.replace('_', ' ')}</Badge>
    </CardContent>
    <CardFooter>
      <Button onClick={() => onUse(template)} className="w-full gap-2">
        <Copy className="h-4 w-4" />
        Use Template
      </Button>
    </CardFooter>
  </Card>
);

// Create Template Dialog
const CreateTemplateDialog = ({ open, onOpenChange, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    age_categories: [],
    gender: 'Mixed',
    format: 'single_elimination',
    registration_fee: 0,
    max_teams: 16,
    fields: [],
    rules: ''
  });
  const [saving, setSaving] = useState(false);
  const [newField, setNewField] = useState('');

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Template name is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(form);
      setForm({
        name: '',
        description: '',
        age_categories: [],
        gender: 'Mixed',
        format: 'single_elimination',
        registration_fee: 0,
        max_teams: 16,
        fields: [],
        rules: ''
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const toggleAgeCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      age_categories: prev.age_categories.includes(cat)
        ? prev.age_categories.filter(c => c !== cat)
        : [...prev.age_categories, cat]
    }));
  };

  const addField = () => {
    if (newField.trim()) {
      setForm(prev => ({
        ...prev,
        fields: [...prev.fields, newField.trim()]
      }));
      setNewField('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Tournament Template</DialogTitle>
          <DialogDescription>
            Save your tournament settings for quick reuse
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Template Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="e.g., Spring Classic Settings"
              data-testid="template-name-input"
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              placeholder="Brief description of this template..."
              rows={2}
            />
          </div>
          
          <div>
            <Label>Age Categories</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {AGE_CATEGORIES.map(cat => (
                <Button
                  key={cat}
                  type="button"
                  variant={form.age_categories.includes(cat) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleAgeCategory(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({...form, gender: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GENDERS.map(g => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Format</Label>
              <Select value={form.format} onValueChange={(v) => setForm({...form, format: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS.map(f => (
                    <SelectItem key={f} value={f} className="capitalize">
                      {f.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Max Teams</Label>
              <Input
                type="number"
                min="2"
                value={form.max_teams}
                onChange={(e) => setForm({...form, max_teams: parseInt(e.target.value) || 16})}
              />
            </div>
            
            <div>
              <Label>Registration Fee ($)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.registration_fee}
                onChange={(e) => setForm({...form, registration_fee: parseFloat(e.target.value) || 0})}
              />
            </div>
          </div>
          
          <div>
            <Label>Fields</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={newField}
                onChange={(e) => setNewField(e.target.value)}
                placeholder="Add field name..."
                onKeyPress={(e) => e.key === 'Enter' && addField()}
              />
              <Button type="button" onClick={addField}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.fields.map((field, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {field}
                  <button onClick={() => setForm({...form, fields: form.fields.filter((_, j) => j !== i)})}>
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          
          <div>
            <Label>Rules / Notes</Label>
            <Textarea
              value={form.rules}
              onChange={(e) => setForm({...form, rules: e.target.value})}
              placeholder="Tournament rules and notes..."
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Use Template Dialog
const UseTemplateDialog = ({ template, open, onOpenChange, onUse }) => {
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    end_date: ''
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      await onUse(template.id, form);
      onOpenChange(false);
    } finally {
      setCreating(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Tournament from Template</DialogTitle>
          <DialogDescription>
            Using template: {template.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Tournament Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="e.g., Spring Classic 2026"
              data-testid="tournament-name-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({...form, start_date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>End Date *</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({...form, end_date: e.target.value})}
              />
            </div>
          </div>
          
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-sm text-muted-foreground mb-2">Template settings that will be applied:</p>
            <div className="flex flex-wrap gap-2 text-xs">
              {template.age_categories?.map(cat => (
                <Badge key={cat} variant="outline">{cat}</Badge>
              ))}
              <Badge variant="secondary">{template.gender}</Badge>
              <Badge>{template.max_teams} teams</Badge>
              <Badge>${template.registration_fee}</Badge>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="gap-2">
            {creating && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Tournament
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TournamentTemplates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [useTemplate, setUseTemplate] = useState(null);

  useEffect(() => {
    if (user) fetchTemplates();
  }, [user]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (data) => {
    try {
      await axios.post(`${API}/templates`, data);
      toast.success('Template saved successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
      throw error;
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template?')) return;
    
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleUseTemplate = async (templateId, data) => {
    try {
      const res = await axios.post(
        `${API}/tournaments/from-template/${templateId}?name=${encodeURIComponent(data.name)}&start_date=${data.start_date}&end_date=${data.end_date}`
      );
      toast.success('Tournament created from template');
      navigate(`/tournaments/${res.data.id}`);
    } catch (error) {
      toast.error('Failed to create tournament');
      throw error;
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">Please login to manage templates</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="h-8 w-8" />
            Tournament Templates
          </h1>
          <p className="text-muted-foreground mt-1">
            Save and reuse tournament settings
          </p>
        </div>
        
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardList className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Templates Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create a template to quickly set up recurring tournaments
            </p>
            <Button onClick={() => setCreateOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onUse={setUseTemplate}
              onDelete={handleDeleteTemplate}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <CreateTemplateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSave={handleSaveTemplate}
      />
      
      <UseTemplateDialog
        template={useTemplate}
        open={!!useTemplate}
        onOpenChange={() => setUseTemplate(null)}
        onUse={handleUseTemplate}
      />
    </div>
  );
};
